import React from "react";
import styled from "styled-components";
import { DropzoneRootProps } from "react-dropzone";
import { PaletteOptions } from "@material-ui/core/styles/createPalette";
import { Box, BoxProps } from "@material-ui/core";
import { calculateRatio, transition } from "styles/utils";

interface DropzoneProps {
    isDisallowed?: boolean;
    isDragAccept: boolean;
    isDragReject: boolean;
    isDragActive: boolean;
    isDisabled: boolean;
    sizeX: number;
    sizeY: number;
}

const getColor = (
    { isDragAccept, isDragReject, isDragActive }: DropzoneRootProps,
    { success, error, primary, grey }: PaletteOptions,
    isTransparent = false
) => {
    if (isDragAccept && success && "main" in success) {
        return success.main;
    }

    if (isDragReject && error && "main" in error) {
        return error.main;
    }

    if (isDragActive && primary && "main" in primary && !isTransparent) {
        return primary.main;
    }

    return isTransparent ? "transparent" : grey?.A100;
};

const calculateSize = (borderSize: number, spacing: number) =>
    borderSize !== 0 ? `${borderSize - spacing}px` : "100%";

export const DropzoneContainer = styled.div<DropzoneRootProps & DropzoneProps>`
    width: ${({ sizeX, spacing = 0, theme }) => calculateSize(sizeX, theme.spacing(spacing))};
    height: ${({ sizeY, spacing = 0, theme }) => calculateSize(sizeY, theme.spacing(spacing))};
    justify-content: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-width: ${({ theme }) => theme.spacing(0.25)}px;
    border-radius: ${({ theme }) => theme.spacing(0.5)}px;
    border-color: ${(props) => getColor(props, props.theme.palette)};
    border-style: dashed;
    background-color: ${({ theme }) => theme.palette.grey["100"]};
    color: ${({ theme }) => theme.palette.grey["400"]};
    outline: none;
    cursor: ${({ isDisallowed }) => (isDisallowed ? "not-allowed" : "pointer")};
    opacity: ${({ isDisabled, isDisallowed }) => {
        if (isDisallowed) {
            return 0;
        }

        return isDisabled ? 0.6 : 1;
    }};

    ${transition(0.24)};
`;

export const ExistingMaterialDropzoneContainer = styled.div<DropzoneRootProps & DropzoneProps>`
    outline: none;
    border-width: ${({ theme }) => theme.spacing(0.25)}px;
    border-radius: ${({ theme }) => theme.spacing(0.5)}px;
    border-color: ${(props) => getColor(props, props.theme.palette, true)};
    border-style: dashed;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};
    width: ${({ sizeX, spacing = 2, theme }) => calculateSize(sizeX, theme.spacing(spacing))};
    height: ${({ sizeY, spacing = 2, theme }) => calculateSize(sizeY, theme.spacing(spacing))};

    ${transition(0.24)};
`;

// height is calculated based on width to fit 16:9 ratio
export const DropzoneBox = styled(({ sizeY, ...props }) => <Box {...props} />)<BoxProps & { sizeY?: number }>`
    ${({ sizeY }) => (sizeY ? calculateRatio(sizeY, 30) : "height: 100%")};
`;
